import React from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { MainContent } from "./components/MainContent";
import { JobSearchLayout } from "./components/JobSearchLayout";
import { RegisterForm } from "./components/RegisterForm";
import { ForgotPasswordForm } from "./components/ForgotPasswordForm";
import { ResetPasswordForm } from "./components/ResetPasswordForm";
import { AuthLandingPage } from "./pages/AuthLandingPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ApplicationsPage } from "./pages/ApplicationsPage";
import { ResumesPage } from "./pages/ResumesPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SettingsPage } from "./pages/SettingsPage";
import { ServicePlansPage } from "./pages/ServicePlansPage";
import { HelpCenterPage } from "./pages/HelpCenterPage";
import { TermsOfServicePage } from "./pages/TermsOfServicePage";
import { CookiePolicyPage } from "./pages/CookiePolicyPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import JobSearchPage from "./pages/JobSearchPage";
import { AgentsPage } from "./pages/AgentsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { LoadingSpinnerExample } from "./components/LoadingSpinnerExample";
import { TestPage } from "./pages/TestPage";
import { SystemHealthCheck } from "./pages/SystemHealthCheck";
import { ApiIntegrationCheckPage } from "./pages/ApiIntegrationCheckPage";
import { TailoredResumeWizardPage } from "./pages/TailoredResumeWizardPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { HourglassSpinnerWrapper } from "./components/HourglassSpinnerWrapper";
import { TourProvider } from "./contexts/TourContext";
import { TourOverlay } from "./components/TourButton";

// Component to conditionally wrap Help page with MainContent for authenticated users
const HelpPageWrapper: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <MainContent activeSection="help">
        <HelpCenterPage />
      </MainContent>
    );
  }

  return <HelpCenterPage />;
};

// Component to conditionally wrap Terms of Service page with MainContent for authenticated users
const TermsPageWrapper: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <MainContent activeSection="settings">
        <TermsOfServicePage />
      </MainContent>
    );
  }

  return <TermsOfServicePage />;
};

// Component to conditionally wrap Cookie Policy page with MainContent for authenticated users
const CookiePageWrapper: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <MainContent activeSection="settings">
        <CookiePolicyPage />
      </MainContent>
    );
  }

  return <CookiePolicyPage />;
};

// Component to conditionally wrap Privacy Policy page with MainContent for authenticated users
const PrivacyPageWrapper: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <MainContent activeSection="settings">
        <PrivacyPolicyPage />
      </MainContent>
    );
  }

  return <PrivacyPolicyPage />;
};

// Component that prevents login page from rendering if user is authenticated
const PublicAuthRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (loading) {
      return;
    }
    if (isAuthenticated) {
      const isLandingPage =
        location.pathname === "/" || location.pathname === "/login";
      if (isLandingPage) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [loading, isAuthenticated, navigate, location.pathname]);

  if (loading) {
    return (
      <HourglassSpinnerWrapper
        fullPage={true}
        title="Signing you in..."
        subtitle="Please wait while we prepare your dashboard"
      />
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

// Paths that are always public and must be viewable without waiting for auth (no login required)
const PUBLIC_PATHS_NO_AUTH = [
  "/privacy-policy",
  "/help",
  "/terms-of-service",
  "/cookie-policy",
  "/developer/api-integration-check",
  "/job-search",
];

// Component to handle smart redirects based on authentication status
const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const isPublicNoAuthPath = PUBLIC_PATHS_NO_AUTH.some((p) =>
    location.pathname.startsWith(p),
  );

  // Show loading screen while auth is being determined, except for public legal/help pages
  // so privacy policy and help are available without logging in
  if (loading && !isPublicNoAuthPath) {
    return (
      <HourglassSpinnerWrapper
        fullPage={true}
        title="Signing you in..."
        subtitle="Please wait while we prepare your dashboard"
      />
    );
  }

  const getBackToLoginRedirect = () => {
    return isAuthenticated ? "/dashboard" : "/login";
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicAuthRoute>
            <AuthLandingPage />
          </PublicAuthRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicAuthRoute>
            <AuthLandingPage />
          </PublicAuthRoute>
        }
      />
      <Route path="/register" element={<RegisterForm showHeader={true} />} />
      <Route
        path="/forgot-password"
        element={
          <ForgotPasswordForm
            onBackToLogin={() =>
              (window.location.href = getBackToLoginRedirect())
            }
            showHeader={true}
          />
        }
      />
      <Route
        path="/reset-password"
        element={
          <ResetPasswordForm
            onBackToLogin={() =>
              (window.location.href = getBackToLoginRedirect())
            }
            showHeader={true}
          />
        }
      />
      {/* Help page - accessible from both public and protected routes */}
      <Route path="/help" element={<HelpPageWrapper />} />
      {/* Terms of Service page - accessible from both public and protected routes */}
      <Route path="/terms-of-service" element={<TermsPageWrapper />} />
      {/* Cookie Policy page - accessible from both public and protected routes */}
      <Route path="/cookie-policy" element={<CookiePageWrapper />} />
      {/* Privacy Policy page - accessible from both public and protected routes */}
      <Route path="/privacy-policy" element={<PrivacyPageWrapper />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <MainContent activeSection="dashboard">
            <DashboardPage />
          </MainContent>
        }
      />
      <Route
        path="/applications"
        element={
          <MainContent activeSection="applications">
            <ApplicationsPage />
          </MainContent>
        }
      />
      <Route
        path="/resumes"
        element={
          <MainContent activeSection="resumes">
            <ResumesPage />
          </MainContent>
        }
      />
      <Route
        path="/profile"
        element={
          <MainContent activeSection="profile">
            <ProfilePage />
          </MainContent>
        }
      />
      <Route
        path="/settings"
        element={
          <MainContent activeSection="settings">
            <SettingsPage />
          </MainContent>
        }
      />
      <Route
        path="/service-plans"
        element={
          <MainContent activeSection="service-plans">
            <ServicePlansPage />
          </MainContent>
        }
      />
      <Route
        path="/job-search/*"
        element={
          <JobSearchLayout>
            <JobSearchPage />
          </JobSearchLayout>
        }
      />
      <Route
        path="/agents"
        element={
          <MainContent activeSection="agents">
            <AgentsPage />
          </MainContent>
        }
      />
      <Route
        path="/developer/api-integration-check/"
        element={<Navigate to="/developer/api-integration-check" replace />}
      />
      <Route
        path="/developer/api-integration-check"
        element={
          <MainContent activeSection="settings">
            <ApiIntegrationCheckPage />
          </MainContent>
        }
      />
      {/* Development/Testing Routes */}
      <Route
        path="/loading-examples"
        element={
          <MainContent>
            <LoadingSpinnerExample />
          </MainContent>
        }
      />
      <Route path="/test" element={<TestPage />} />
      {/* System Health Check (hidden admin page) */}
      <Route path="/system-health" element={<SystemHealthCheck />} />
      {/* Tailored Resume Wizard (opens in new window) */}
      <Route
        path="/tailored-resume-wizard/:jobApplicationId"
        element={<TailoredResumeWizardPage />}
      />

      {/* Catch all route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LoadingProvider>
          <SubscriptionProvider>
            <TourProvider>
              <AppRoutes />
              <TourOverlay />
            </TourProvider>
          </SubscriptionProvider>
        </LoadingProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
